import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeftLong } from "@fortawesome/pro-regular-svg-icons";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import { useAuth } from "../../providers/AuthContext";
import LoadingTablePage from "../LoadingTablePage";
import Breadcrumbs from "../../components/Breadcrumbs";
import { getManagedDeviceByPublicId } from "../../api/ManagedDevices";
import FormatBytes from "../../helpers/FormatBytes";

const DeviceInventoryDetailPage = () => {
  const { getAccessToken } = useAuth();
  const { managedDevicePublicId, agreementPublicId } = useParams();
  const navigate = useNavigate();
  const [device, setDevice] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    handleGetDevice();
  }, []);

  const handleGetDevice = async () => {
    try {
      const accessToken = await getAccessToken();
      const data = await getManagedDeviceByPublicId(managedDevicePublicId, accessToken);
      setDevice(data);
      setLoading(false);
    } catch (error) {
      console.error("Failed to fetch managed device:", error);
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "–";
    return new Date(dateString).toLocaleString();
  };

  return (
    <>
      <HeaderBar />
      <div className="flex flex-row w-full">
        <Sidebar />
        <main className="page">
          <div className="absolute w-full pointer-events-none">
            <LoadingTablePage title={"Device details"} loading={loading} />
          </div>
          <div className={`${loading ? "loading" : ""} page-content`}>
            <Breadcrumbs />

            <div className="flex flex-row items-center pb-4">
              <button
                onClick={() => navigate(`/${agreementPublicId}/device-inventory`)}
                className="text-black flex"
              >
                <FontAwesomeIcon icon={faArrowLeftLong} className="w-4 pr-4" />
              </button>
              <h2 className="border-l border-mute-200 pl-4">
                {device?.deviceName || "Device details"}
              </h2>
            </div>

            {device && (
              <div className="flex flex-col gap-6">
                {/* General */}
                <Section title="General">
                  <Field label="Device name" value={device.deviceName} />
                  <Field label="Manufacturer" value={device.manufacturer} />
                  <Field label="Model" value={device.model} />
                  <Field label="Serial number" value={device.serialNumber} />
                  <Field label="Operating system" value={device.operatingSystem} />
                  <Field label="OS version" value={device.osVersion} />
                  <Field label="Join type" value={device.joinType} />
                </Section>

                {/* Primary user */}
                <Section title="Primary user">
                  <Field label="Display name" value={device.primaryUserDisplayName} />
                  <Field label="Email" value={device.primaryUserEmail} />
                </Section>

                {/* Hardware */}
                <Section title="Hardware">
                  <Field label="Total storage" value={FormatBytes(device.totalStorageSpaceInBytes)} />
                  <Field label="Free storage" value={FormatBytes(device.freeStorageSpaceInBytes)} />
                  <Field label="Physical memory" value={FormatBytes(device.physicalMemoryInBytes)} />
                  <Field label="CPU model" value={device.cpuModel} />
                  <Field label="CPU cores" value={device.cpuCoreCount} />
                  <Field
                    label="Clock speed"
                    value={device.cpuMaxClockSpeedMhz ? `${device.cpuMaxClockSpeedMhz} MHz` : null}
                  />
                </Section>

                {/* Security */}
                <Section title="Security">
                  <Field label="Compliance state" value={device.complianceState} />
                  <Field
                    label="Encrypted"
                    value={device.isEncrypted != null ? (device.isEncrypted ? "Yes" : "No") : null}
                  />
                </Section>

                {/* Timestamps */}
                <Section title="Timestamps">
                  <Field label="Enrolled" value={formatDate(device.enrolledDateTime)} />
                  <Field label="Last sync" value={formatDate(device.lastSyncDateTime)} />
                  <Field label="Created" value={formatDate(device.createdDateTimeUTC)} />
                  <Field label="Updated" value={formatDate(device.updatedDateTimeUTC)} />
                </Section>
              </div>
            )}
          </div>
        </main>
      </div>
    </>
  );
};

const Section = ({ title, children }) => (
  <div className="rounded-md shadow-md border w-full p-4">
    <h3 className="text-lg font-semibold pb-3 border-b border-mute-200 mb-3">{title}</h3>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
      {children}
    </div>
  </div>
);

const Field = ({ label, value }) => (
  <div className="flex flex-col">
    <span className="text-xs text-mute-500">{label}</span>
    <span className="text-sm">{value ?? "–"}</span>
  </div>
);

export default DeviceInventoryDetailPage;
